import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import Moment from 'moment'
import {
  Button,
  Checkbox,
  Column,
  Columns,
  Container,
  Switch,
  TextInput,
} from '@resurs/midas-react'
import Select from '../../../midasComponents/components/Select'
import Page from '../../../internal/Page'
import TextEditor from '../../../internal/TextEditor'
import DatePicker from '../../../internal/Datepicker'
import { format } from '../../../models/format'
import { getText } from '../../../internal/TextLabels'
import { tryFindName } from '../../../util/helpers'
import EditNewsTarget from './EditNewsTarget'
import NewsTargetInfo from './NewsTargetInfo'
import { useOnUpdate, useToggle } from '../../../util/utility-hooks'
import {
  getScopeValueFromTags,
  getTypeValueFromTags,
  getSelectScopeValues,
  getTypeSelectValues,
  isIncidentTypeAndResursAdmin,
  isResursAdmin,
  isIncidentType,
  getArticleBannerTag,
  getTypeFromTypeValue,
} from '../utils'
import {
  ArticleShowBannerValue,
  DefaultArticleScopeValue,
  DefaultArticleTypeValue,
} from '../constants'
import { Features, isEnabled } from '../../../models/features'
import ArticleNotificationUI from '../../../internal/ArticleNotification/UI'

const DATE_PICKER_DATE_FORMAT = 'yyyy-MM-dd HH:mm'

const hasTag = (entry, tag) => {
  return entry[tag]
}

const getTargetConfig = targetList => {
  let resultList = []
  if (targetList) {
    resultList = targetList.filter(entry => !entry['everyone'])
  }
  if (resultList.length) {
    const entry = resultList[0]

    if (hasTag(entry, 'country')) {
      return 'countries'
    }
    if (hasTag(entry, 'representative')) {
      return 'representatives'
    }
    if (hasTag(entry, 'store')) {
      return 'stores'
    }
    if (hasTag(entry, 'role')) {
      return 'roles'
    }
  }
  return 'all'
}

const getTargetList = (targetList, tag) => {
  const resultList = []
  if (targetList) {
    targetList
      .filter(entry => entry[tag])
      .forEach(entry => {
        resultList.push({ id: entry[tag] })
      })
  }
  return resultList
}

const EditNewsForm = ({
  role,
  representatives,
  stores,
  newsStores: propsNewsStores,
  getRepresentativeStores,
  getNewsStores,
  onSave: propsOnSave,
  article,
}) => {
  const [articleId, setArticleId] = useState(null)
  const [message, setMessage] = useState({ title: '', text: '' })
  const [type, setType] = useState(DefaultArticleTypeValue)
  const [banner, setBanner] = useState(null)
  const [scope, setScope] = useState(null)
  const [showBannerPreview, setShowBannerPreview] = useState(false)
  const [time, setTime] = useState({
    startDate: Moment().startOf('day'),
    endDate: Moment().add(1, 'day').endOf('day'),
    untilFurtherNotice: false,
  })
  const [targetData, setTargetData] = useState({
    target: '',
    roleTarget: '',
    newsCountries: [],
    newsRepresentatives: [],
    newsStores: [],
    newsRoles: [],
  })

  const [targetModal, toggleTargetModal] = useToggle(false)

  const [published, setPublished] = useState(false)

  const onSave = () => {
    propsOnSave({
      id: articleId,
      message,
      tags: [type, scope, banner].filter(obj => !!obj),
      time,
      targetData,
      published,
    })
  }

  const handleChangeType = newType => {
    setType(newType)
    if (isIncidentTypeAndResursAdmin(newType, role)) {
      setBanner(ArticleShowBannerValue)
    }
  }

  const isButtonDisabled = Object.values(targetData).every(
    value => !value || (Array.isArray(value) && value.length === 0),
  )

  useOnUpdate(() => {
    if (!banner) {
      setShowBannerPreview(false)
      setScope(null)
    }
  }, [banner])

  useEffect(() => {
    if (!article) {
      return
    }
    const targetStores = getTargetList(article.primaryAudiences, 'store')
    if (targetStores && targetStores.length) {
      getNewsStores(targetStores.map(store => store.id)).then(nextStores =>
        setTargetData({ ...targetData, newsStores: nextStores }),
      )
    }
    setArticleId(article.id)
    setMessage({ title: article.header, text: article.body })
    setType(getTypeValueFromTags(article.tags))
    const newBannerTag = getArticleBannerTag(article.tags)
    setBanner(newBannerTag)
    setScope(
      newBannerTag
        ? getScopeValueFromTags(article.tags) || DefaultArticleScopeValue
        : null,
    )
    setTime({
      startDate: Moment(article.publicationStart).startOf('day'),
      endDate:
        (!!article.publicationEnd &&
          Moment(article.publicationEnd).endOf('day')) ||
        null,
      untilFurtherNotice: !article.publicationEnd,
    })
    setTargetData({
      target: getTargetConfig(article.primaryAudiences),
      roleTarget: getTargetConfig(article.secondaryAudiences),
      newsCountries: getTargetList(article.primaryAudiences, 'country'),
      newsRepresentatives: getTargetList(
        article.primaryAudiences,
        'representative',
      ).map(representative => ({
        id: representative.id,
        name: tryFindName(representative.id, representatives),
      })),
      newsStores: propsNewsStores || [],
      newsRoles: getTargetList(article.secondaryAudiences, 'role'),
    })
    setPublished(article.visible)
  }, [article])

  const handleChangeStartDate = date => {
    const startDate = Moment(date).isBefore(Moment())
      ? Moment().startOf('day')
      : Moment(date).startOf('day')

    const endDate = !time.untilFurtherNotice
      ? Moment(time.endDate).isBefore(startDate)
        ? Moment(startDate).add(1, 'day').endOf('day')
        : time.endDate
      : null

    setTime({ ...time, startDate, endDate })
  }

  const handleChangeEndDate = date => {
    const endDate = Moment(date).isBefore(time.startDate)
      ? Moment(time.startDate).add(1, 'day').endOf('day')
      : Moment(date).endOf('day')

    setTime({ ...time, endDate })
  }

  const handleToggleUntilFurtherNotice = () => {
    setTime({
      ...time,
      endDate: time.untilFurtherNotice
        ? Moment(time.startDate).add(1, 'day').endOf('day')
        : null,
      untilFurtherNotice: !time.untilFurtherNotice,
    })
  }

  let { newsStores } = targetData
  newsStores = newsStores.length > 0 ? newsStores : propsNewsStores
  return (
    <Container>
      {!!banner && showBannerPreview && (
        <div style={{ margin: '0 -1rem' }}>
          <ArticleNotificationUI
            title={message.title}
            body={message.text}
            type={getTypeFromTypeValue(type)}
            published={Moment().format()}
            onClose={() => setShowBannerPreview(false)}
          />
        </div>
      )}
      <Columns>
        <Column col2 colMd0 />
        <Column col8 colMd12>
          <Page
            title={
              article ? getText('news.edit.title') : getText('news.add.title')
            }
          >
            <Container grid960>
              <Columns>
                <Column col1 colXl0 />
                <Column col10 colXl12>
                  <TextInput
                    id="add-edit-news-form-header"
                    label={getText('news.add.messageSection.header')}
                    format={format.punctuationString}
                    value={message.title}
                    onChange={e =>
                      setMessage({ ...message, title: e.target.value })
                    }
                  />

                  <TextEditor
                    id="newsText"
                    fieldName="add-edit-news-form-texteditor"
                    fieldCaption={getText('news.add.messageSection.text')}
                    defaultValue={article ? article.body : ''}
                    onChange={text => setMessage({ ...message, text })}
                  />
                  <div style={{ paddingTop: '1rem' }}>
                    <Select
                      id="newsType"
                      label={getText('news.add.typeSection.type')}
                      value={type}
                      values={getTypeSelectValues(role)}
                      onChange={handleChangeType}
                    />
                  </div>
                  {isEnabled(Features.INCIDENT_NOTIFICATION) &&
                    isResursAdmin(role) && (
                      <div
                        style={{
                          display: 'flex',
                        }}
                      >
                        <Checkbox
                          id="banner"
                          checked={!!banner}
                          text={getText('news.add.bannerSection.label')}
                          disabled={isIncidentType(type)}
                          onChange={e =>
                            setBanner(
                              e.target.checked ? ArticleShowBannerValue : null,
                            )
                          }
                        />
                        {!!banner && (
                          <div style={{ margin: '1.1rem 0 0 -1rem' }}>
                            <Button
                              type="link"
                              size="small"
                              text={
                                showBannerPreview
                                  ? getText(
                                      'news.add.bannerSection.preview.hide',
                                    )
                                  : getText(
                                      'news.add.bannerSection.preview.show',
                                    )
                              }
                              onClick={() =>
                                setShowBannerPreview(!showBannerPreview)
                              }
                            />
                          </div>
                        )}
                      </div>
                    )}
                  {isEnabled(Features.INCIDENT_NOTIFICATION) &&
                    isResursAdmin(role) &&
                    banner && (
                      <div style={{ paddingTop: '1rem' }}>
                        <Select
                          id="scope"
                          label={getText('news.add.scopeSection.label')}
                          value={scope || ''}
                          values={getSelectScopeValues()}
                          onChange={setScope}
                        />
                      </div>
                    )}
                  <div style={{ paddingTop: '4rem' }}>
                    <div>{getText('news.add.timeSection.subtitle')}</div>
                    <Columns>
                      <Column col4>
                        <DatePicker
                          name="add-edit-news-form-startDate"
                          label={getText('news.add.timeSection.startDate')}
                          minDate={Moment()}
                          selected={time.startDate}
                          dateFormat={DATE_PICKER_DATE_FORMAT}
                          onChange={handleChangeStartDate}
                        />
                      </Column>
                      <Column col8>
                        <div style={{ display: 'flex' }}>
                          {!time.untilFurtherNotice && (
                            <div style={{}}>
                              <DatePicker
                                name="add-edit-news-form-endDate"
                                label={getText('news.add.timeSection.endDate')}
                                minDate={time.startDate}
                                selected={time.endDate}
                                dateFormat={DATE_PICKER_DATE_FORMAT}
                                onChange={handleChangeEndDate}
                              />
                            </div>
                          )}
                          <div style={{ padding: '2.4rem 2rem' }}>
                            <Checkbox
                              id="indefinite"
                              checked={time.untilFurtherNotice}
                              text={getText(
                                'news.add.timeSection.untilFurtherNotice',
                              )}
                              onChange={handleToggleUntilFurtherNotice}
                            />
                          </div>
                        </div>
                      </Column>
                    </Columns>
                  </div>
                  <div>
                    <div style={{ paddingLeft: '1rem' }}>
                      <b>{getText('news.add.recipientSection.filterChoice')}</b>
                      <div>
                        <Button
                          size="small"
                          text={getText(
                            'news.add.recipientSection.limitTarget',
                          )}
                          onClick={toggleTargetModal}
                        />
                        <NewsTargetInfo
                          targetData={{ ...targetData, newsStores }}
                          stores={stores}
                          representatives={representatives}
                        />
                      </div>
                    </div>

                    {targetModal && (
                      <EditNewsTarget
                        title={getText(
                          'news.add.recipientSection.targetHeader',
                        )}
                        onBack={toggleTargetModal}
                        onSave={data => {
                          setTargetData({
                            ...targetData,
                            ...data,
                          })
                          toggleTargetModal()
                        }}
                        target={targetData.target}
                        roleTarget={targetData.roleTarget}
                        newsStores={newsStores}
                        newsCountries={targetData.newsCountries}
                        newsRoles={targetData.newsRoles}
                        newsRepresentatives={targetData.newsRepresentatives}
                        stores={stores}
                        representatives={representatives}
                        getRepresentativeStores={representativeId =>
                          getRepresentativeStores(representativeId)
                        }
                      />
                    )}
                  </div>
                  <div style={{ display: 'flex', paddingTop: '4rem' }}>
                    <div style={{ marginLeft: 'auto' }}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Switch
                          id="published"
                          text={getText('news.add.publishSection.published')}
                          checked={published}
                          onChange={() => setPublished(!published)}
                        />

                        <Button
                          text={getText('default.save')}
                          type="primary"
                          disabled={
                            isButtonDisabled ||
                            !message.text?.trim() ||
                            !message.title?.trim()
                          }
                          onClick={() => onSave()}
                        />
                      </div>
                    </div>
                  </div>
                </Column>
              </Columns>
            </Container>
          </Page>
        </Column>
      </Columns>
    </Container>
  )
}

EditNewsForm.propTypes = {
  role: PropTypes.string.isRequired,
  representatives: PropTypes.array.isRequired,
  stores: PropTypes.array.isRequired,
  newsStores: PropTypes.array.isRequired,
  getRepresentativeStores: PropTypes.func.isRequired,
  getNewsStores: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  article: PropTypes.object,
}

EditNewsForm.defaultProps = {
  article: null,
}

export default EditNewsForm
